import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, Shield, Zap } from "lucide-react";

interface NotificationPermissionPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestPermission: () => void;
  streakCount?: number;
  vapidConfigured?: boolean;
}

export function NotificationPermissionPrompt({
  open,
  onOpenChange,
  onRequestPermission,
  streakCount = 0,
  vapidConfigured = true,
}: NotificationPermissionPromptProps) {
  // Don't render if VAPID is not configured
  if (!vapidConfigured) {
    return null;
  }
  const handleEnable = () => {
    onRequestPermission();
    onOpenChange(false);
  };

  const handleMaybeLater = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Bell className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">
            Never Lose Your Streak!
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {streakCount > 0 
              ? `Protect your ${streakCount}-day streak with daily reminders`
              : "Get notified when new puzzles are ready and protect your future streaks"
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <div className="flex items-start gap-3">
            <div className="mt-1">
              <Zap className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <h4 className="font-medium">Daily Puzzle Alerts</h4>
              <p className="text-sm text-muted-foreground">
                Get notified at 9am when today's puzzle is ready
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="mt-1">
              <Shield className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <h4 className="font-medium">Streak Protection</h4>
              <p className="text-sm text-muted-foreground">
                Evening reminder at 9pm if you haven't played yet
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="mt-1">
              <Bell className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium">Achievement Unlocks</h4>
              <p className="text-sm text-muted-foreground">
                Celebrate your wins with instant notifications
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Button onClick={handleEnable} className="w-full gap-2" size="lg">
            <Bell className="h-4 w-4" />
            Enable Notifications
          </Button>
          <Button onClick={handleMaybeLater} variant="ghost" className="w-full">
            Maybe Later
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          You can customize notification types in Settings anytime
        </p>
      </DialogContent>
    </Dialog>
  );
}
